import api, { ApiResponse } from './index'
import { Application, CreateApplicationData, ApplicationStats, WorkSubmissionData, ReviewData } from '@/types/application'
import { accessLimitService } from '../accessLimitService'

export const applicationApi = {
    // Get all applications for current user
    getApplications: (params: {
        page?: number
        limit?: number
        status?: string
        sortBy?: string
    } = {}): Promise<ApiResponse<{
        applications: Application[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/applications', { params }),

    // Get applications sent by current user (worker applications)
    getSentApplications: (params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        applications: Application[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/applications/sent', { params }),

    // Get applications received by current user (employer's received applications)
    getReceivedApplications: (params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        applications: Application[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/applications/received', { params }),

    // Get single application by ID
    getApplicationById: (id: string): Promise<ApiResponse<{ application: Application }>> =>
        api.get(`/applications/${id}`),

    // Create new application
    createApplication: async (data: CreateApplicationData): Promise<ApiResponse<{ application: Application }>> => {
        try {
            return await api.post('/applications', data);
        } catch (error: any) {
            // Check if this is an access limit error and enhance the error message
            const accessLimitError = accessLimitService.handleAccessLimitError(error);
            if (accessLimitError.isAccessLimitError) {
                // Add enhanced error information for the frontend to handle
                error.accessLimitError = accessLimitError;
            }
            throw error;
        }
    },

    // Update application status (for employers)
    updateApplicationStatus: (
        id: string,
        status: 'accepted' | 'rejected' | 'completed',
        message?: string
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.put(`/applications/${id}/status`, { status, message }),

    // Withdraw application (for workers)
    withdrawApplication: (id: string): Promise<ApiResponse> =>
        api.delete(`/applications/${id}`),

    // Update application details
    updateApplication: (
        id: string,
        data: Partial<CreateApplicationData>
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.put(`/applications/${id}`, data),

    // Get application statistics
    getApplicationStats: (): Promise<ApiResponse<{ stats: ApplicationStats }>> =>
        api.get('/applications/stats'),

    // Rate completed application
    rateApplication: (
        id: string,
        rating: {
            score: number
            comment: string
        }
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/rate`, rating),

    // Get applications for a specific gig (for gig owners)
    getGigApplications: (gigId: string, params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        applications: Application[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/applications/gig/${gigId}`, { params }),

    // Accept multiple applications
    bulkUpdateStatus: (
        applicationIds: string[],
        status: 'accepted' | 'rejected',
        message?: string
    ): Promise<ApiResponse<{ applications: Application[] }>> =>
        api.put('/applications/bulk-status', { applicationIds, status, message }),

    // Get application timeline/activity
    getApplicationActivity: (id: string): Promise<ApiResponse<{
        activities: Array<{
            _id: string
            action: string
            description: string
            performedBy: string
            performedAt: string
            metadata?: any
        }>
    }>> =>
        api.get(`/applications/${id}/activity`),

    // Mark application as completed by worker
    markAsCompleted: (
        id: string,
        completionNote?: string,
        attachments?: string[]
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/complete`, { completionNote, attachments }),

    // Request application modification
    requestModification: (
        id: string,
        message: string,
        requestedChanges: string[]
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/modify`, { message, requestedChanges }),

    // Get application metrics for dashboard
    getApplicationMetrics: (dateRange?: {
        start: string
        end: string
    }): Promise<ApiResponse<{
        totalApplications: number
        acceptanceRate: number
        completionRate: number
        averageResponseTime: number
        applicationsByStatus: Record<string, number>
        applicationsByCategory: Record<string, number>
        applicationsOverTime: Array<{ date: string; count: number }>
    }>> =>
        api.get('/applications/metrics', { params: dateRange }),

    // Submit work for accepted application
    submitWork: (
        id: string,
        data: WorkSubmissionData
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/submit-work`, data),

    // Review submitted work (for employers)
    reviewWork: (
        id: string,
        data: ReviewData
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/review`, data),

    // Request changes for submitted work (for employers)
    requestChanges: (
        id: string,
        feedback: string,
        changesRequested?: string[]
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/request-changes`, { feedback, changesRequested }),

    // Resubmit work after changes requested (for workers)
    resubmitWork: (
        id: string,
        data: WorkSubmissionData
    ): Promise<ApiResponse<{ application: Application }>> =>
        api.post(`/applications/${id}/resubmit-work`, data),

    // Export applications data
    exportApplications: (params: {
        format: 'csv' | 'excel'
        dateRange?: { start: string; end: string }
        status?: string
    }): Promise<Blob> =>
        api.get('/applications/export', {
            params,
            responseType: 'blob'
        }).then(response => response.data),
}